import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Egg, Plus, Calendar, Users, ArrowRight } from 'lucide-react';

const STATUS_LABELS = {
  planned: 'Planeada', paired: 'Emparejada', ovulating: 'Ovulando',
  laid: 'Puesta', incubating: 'Incubando', hatching: 'Por nacer',
  available: 'Disponible', assigned: 'Asignada', completed: 'Completada',
};

const STATUS_COLORS = {
  planned: 'bg-zinc-500/10 text-zinc-400', paired: 'bg-blue-500/10 text-blue-400',
  ovulating: 'bg-purple-500/10 text-purple-400', laid: 'bg-amber-500/10 text-amber-400',
  incubating: 'bg-orange-500/10 text-orange-400', hatching: 'bg-emerald-500/10 text-emerald-400',
  available: 'bg-emerald-600/10 text-emerald-300', assigned: 'bg-teal-500/10 text-teal-400',
  completed: 'bg-zinc-600/10 text-zinc-500',
};

export default function MyClutchesPage() {
  const navigate = useNavigate();
  const [clutches, setClutches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMyClutches({})
      .then(data => setClutches(data.clutches || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="my-clutches-page" className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-light tracking-tight text-zinc-100 font-[Outfit]">
            <Egg className="w-6 h-6 inline mr-2 text-emerald-400" />
            Mis Camadas
          </h1>
          <p className="text-zinc-500 mt-1">Pre-venta y waitlist de camadas futuras</p>
        </div>
        <Button onClick={() => navigate('/dashboard/camadas/nueva')} className="bg-emerald-600 text-white" data-testid="new-clutch-btn">
          <Plus className="w-4 h-4 mr-2" />Nueva Camada
        </Button>
      </div>

      {loading ? (
        <p className="text-zinc-500 text-center py-12">Cargando...</p>
      ) : clutches.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Egg className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">No tienes camadas publicadas</p>
          <Button onClick={() => navigate('/dashboard/camadas/nueva')} className="bg-emerald-600 text-white">
            Crear primera camada
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {clutches.map(c => (
            <Link key={c.id} to={`/dashboard/camadas/${c.id}`}
              className="glass-card p-4 flex items-center gap-4 hover:bg-white/[0.03] transition-colors block" data-testid={`clutch-card-${c.id}`}>
              {c.parent_images?.[0] ? (
                <img src={c.parent_images[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center">
                  <Egg className="w-6 h-6 text-zinc-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-zinc-100 font-medium">{c.species_name} {c.morph_expected}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || STATUS_COLORS.planned}`}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 flex gap-3">
                  <span>{c.sire_info} x {c.dam_info}</span>
                  {c.estimated_hatch_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{c.estimated_hatch_date}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.waitlist_count || 0} en waitlist</span>
                </div>
                {c.price_range_min > 0 && <p className="text-xs text-zinc-400 mt-0.5">${c.price_range_min} - ${c.price_range_max} MXN</p>}
              </div>
              <ArrowRight className="w-4 h-4 text-zinc-600" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
